import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import type { ListQuery } from '../../core/types/api';
import type { CrudService } from '../../core/services/crud.service';

interface EntitySelectProps<T extends { id: number }> {
  /** Stable query cache key — share with other readers of the same list. */
  cacheKey: readonly unknown[];
  service: CrudService<T, Partial<T>, Partial<T>>;
  query?: ListQuery;
  value: number | null | undefined;
  onChange: (id: number | null) => void;
  label: string;
  helperText?: string;
  getOptionLabel?: (entity: T) => string;
  excludeId?: number;
  required?: boolean;
  disabled?: boolean;
}

/**
 * Generic Autocomplete that loads its options from a `CrudService`. Backed
 * by TanStack Query so multiple selects for the same resource share a
 * single fetch via the same cache key.
 */
export function EntitySelect<T extends { id: number; name?: string }>({
  cacheKey,
  service,
  query,
  value,
  onChange,
  label,
  helperText,
  getOptionLabel,
  excludeId,
  required,
  disabled,
}: EntitySelectProps<T>) {
  const list = useQuery({
    queryKey: cacheKey,
    queryFn: () => service.list({ pageSize: 200, sort: 'name', ...query }),
  });

  const options = (list.data?.data ?? []).filter((o) => o.id !== excludeId);
  const selected = options.find((o) => o.id === value) ?? null;
  const labelFor = getOptionLabel ?? ((o: T) => o.name ?? `#${o.id}`);

  return (
    <Autocomplete<T, false, false, false>
      fullWidth
      disabled={disabled}
      options={options}
      value={selected}
      onChange={(_, next) => onChange(next?.id ?? null)}
      getOptionLabel={labelFor}
      isOptionEqualToValue={(a, b) => a.id === b.id}
      loading={list.isLoading}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          required={required}
          helperText={helperText}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {list.isLoading ? <CircularProgress size={16} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
}
